import PropTypes from 'prop-types';
import {Global, css} from '@emotion/react';
import {useMemo, useCallback, useState, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useIntl} from 'react-intl';
import debounce from 'lodash/debounce';

import {
  Box, Button, Flex, Heading, Skeleton, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {useBlogsPagesByTags, PAGE_SIZE} from '@amblique/king-living/app/contentful/hooks';
import {BlogGridListing} from '@amblique/king-living/app/contentful/models/blog-grid/blog-grid-listing';
import {categoryTagTermFromDisplayName} from '@amblique/king-living/app/contentful/utils/blog-metadata-tag-ids';

const editorialListingHeroBackgroundReset = css`
  body:has(.component--blog-listing) .component--hero-banner > div:first-of-type {
    background-color: transparent;
  }
`;

export const BlogListing = ({component, isComplete = true, isFetching = false, ...props}) => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const [currentPage, setCurrentPage] = useState(0);
  const [accumulatedBlogs, setAccumulatedBlogs] = useState([]);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  const filterTags = useMemo(() => component.filterTags || [], [component.filterTags]);

  const styles = useMultiStyleConfig('contentful/blog-listing', {
    component,
    isComplete,
    isFetching,
  });

  const selectedTagsFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const tagParam = urlParams.get('tag');

    return tagParam ? tagParam.split(',').filter(Boolean) : [];
  }, [location.search]);

  const tagsToQuery = useMemo(
    () => (selectedTagsFromUrl.length > 0 ? selectedTagsFromUrl : filterTags),
    [selectedTagsFromUrl, filterTags]
  );

  // Initialized from tagsToQuery so the first query on mount (including back-navigation remount)
  // uses the correct tags immediately, avoiding a debounce-induced double-reset cycle.
  const [debouncedTagsToQuery, setDebouncedTagsToQuery] = useState(tagsToQuery);

  const debouncedSetTags = useMemo(
    () => debounce((tags) => {
      setDebouncedTagsToQuery(tags);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSetTags(tagsToQuery);

    return () => {
      debouncedSetTags.cancel();
    };
  }, [tagsToQuery, debouncedSetTags]);

  const {data: pagesCollection, isLoading: blogsLoading} = useBlogsPagesByTags(
    debouncedTagsToQuery,
    {limit: PAGE_SIZE, skip: currentPage * PAGE_SIZE}
  );

  const currentBatch = useMemo(() => {
    if (!pagesCollection?.items) return [];

    return pagesCollection.items
      .filter(Boolean)
      .map((page) => {
        const blogEntry = page.mainRegionCollection?.items?.find(
          (item) => item?.__typename === 'Blog'
        );

        if (!blogEntry) return null;

        return {
          ...blogEntry,
          pageSlug: page.slug,
          pageTitle: page.title,
          pageTags: page.contentfulMetadata?.tags || [],
        };
      })
      .filter(Boolean);
  }, [pagesCollection?.items]);

  const tagsQueryKey = useMemo(() => debouncedTagsToQuery.join(','), [debouncedTagsToQuery]);

  useEffect(() => {
    setCurrentPage(0);
    setAccumulatedBlogs([]);
    setHasReachedEnd(false);
  }, [tagsQueryKey]);

  useEffect(() => {
    // Skip empty batches on load-more (query still loading)
    if (currentBatch.length === 0 && currentPage > 0) return;

    if (currentPage === 0) {
      setAccumulatedBlogs((prev) => {
        if (prev.length !== currentBatch.length) return currentBatch;
        const prevKey = prev.map((b) => b.pageSlug).join(',');
        const newKey = currentBatch.map((b) => b.pageSlug).join(',');

        return newKey !== prevKey ? currentBatch : prev;
      });
    } else {
      setAccumulatedBlogs((prev) => {
        const existingSlugs = new Set(prev.map((blog) => blog.pageSlug));
        const newBlogs = currentBatch.filter((blog) => !existingSlugs.has(blog.pageSlug));

        if (newBlogs.length === 0) {
          setHasReachedEnd(true);
        }

        return newBlogs.length > 0 ? [...prev, ...newBlogs] : prev;
      });

      if (currentBatch.length < PAGE_SIZE) {
        setHasReachedEnd(true);
      }
    }
  }, [currentBatch, currentPage]);

  const totalCount = pagesCollection?.total || 0;
  const showingCount = accumulatedBlogs.length;
  const hasMore = !hasReachedEnd && showingCount < totalCount;

  const handleTagToggle = useCallback((tagName) => {
    const params = new URLSearchParams(location.search);
    const currentTags = selectedTagsFromUrl;
    const normalizedTag = categoryTagTermFromDisplayName(tagName);
    const isSelected = currentTags.includes(normalizedTag) || currentTags.includes(tagName);

    if (isSelected) {
      const newTags = currentTags.filter((t) => t !== normalizedTag && t !== tagName);

      if (newTags.length > 0) {
        params.set('tag', newTags.join(','));
      } else {
        params.delete('tag');
      }
    } else {
      params.set('tag', [...currentTags, normalizedTag].join(','));
    }

    history.replace({search: params.toString()});
  }, [selectedTagsFromUrl, location.search, history]);

  const handleClearFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);

    params.delete('tag');
    history.replace({search: params.toString()});
  }, [location.search, history]);

  const handleLoadMore = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const isInitialLoad = !isComplete && isFetching;
  const isFilterOrPaginationLoading = blogsLoading && currentPage === 0;

  if (isInitialLoad) {
    return (
      <>
        <Global styles={editorialListingHeroBackgroundReset} />
        <Box {...styles.root} {...props}>
          <Skeleton height="600px" width="100%" />
        </Box>
      </>
    );
  }

  const renderContent = () => {
    if (isFilterOrPaginationLoading) {
      return (
        <Box className="blog-listing__content-skeleton" {...styles.contentSkeleton}>
          <Skeleton height="400px" width="100%" />
        </Box>
      );
    }

    if (accumulatedBlogs.length === 0) {
      return (
        <Box className="blog-listing__empty-state" {...styles.emptyState} role="status">
          <Heading as="h2" mb={4} size="md">
            {intl.formatMessage({
              id: 'blog_listing.no_results.heading',
              defaultMessage: 'No articles found',
            })}
          </Heading>
          <Text mb={4}>
            {intl.formatMessage({
              id: 'blog_listing.no_results.description',
              defaultMessage: 'No articles found matching the selected filters.',
            })}
          </Text>
          {selectedTagsFromUrl.length > 0 && (
            <Button
              variant="link"
              aria-label={intl.formatMessage({
                id: 'blog_listing.clear_filters.aria_label',
                defaultMessage: 'Clear all applied filters',
              })}
              onClick={handleClearFilters}
            >
              {intl.formatMessage({
                id: 'blog_listing.clear_filters',
                defaultMessage: 'Clear Filters',
              })}
            </Button>
          )}
        </Box>
      );
    }

    return (
      <>
        <Box className="blog-listing__grid" {...styles.gridContainer}>
          <BlogGridListing
            isComplete={isComplete}
            isFetching={isFetching}
            styles={styles}
            component={{
              blogSummaryCollection: {
                items: accumulatedBlogs,
              },
            }}
          />
        </Box>

        {hasMore && (
          <Flex className="blog-listing__load-more" {...styles.loadMoreContainer}>
            <Button
              isLoading={blogsLoading && currentPage > 0}
              variant="filled-dark"
              aria-label={intl.formatMessage({
                id: 'blog_listing.load_more.aria_label',
                defaultMessage: 'Show more articles',
              })}
              onClick={handleLoadMore}
            >
              {intl.formatMessage({
                id: 'blog_listing.load_more',
                defaultMessage: 'Show More Articles',
              })}
            </Button>
          </Flex>
        )}
      </>
    );
  };

  return (
    <>
      <Global styles={editorialListingHeroBackgroundReset} />
      <Box className="blog-listing" {...styles.root} {...props}>
      {filterTags.length > 0 && (
        <Flex
          className="blog-listing__filter-section-wrapper"
          {...styles.filterSectionWrapper}
        >
          <Flex
            as="nav"
            className="blog-listing__filter-tags"
            {...styles.filterSection}
            aria-label={intl.formatMessage({
              id: 'blog_listing.filter_tags.aria_label',
              defaultMessage: 'Filter articles by tag',
            })}
          >
            {filterTags.map((tag) => {
              const isActive =
                selectedTagsFromUrl.includes(categoryTagTermFromDisplayName(tag)) ||
                selectedTagsFromUrl.includes(tag);

              return (
                <Box
                  key={tag}
                  aria-pressed={isActive}
                  as="button"
                  className="blog-listing__filter-chip"
                  data-active={isActive ? 'true' : 'false'}
                  type="button"
                  {...styles.filterChip}
                  aria-label={intl.formatMessage(
                    {
                      id: 'blog_listing.filter_by.aria_label',
                      defaultMessage: 'Filter by {tag}',
                    },
                    {tag}
                  )}
                  onClick={() => handleTagToggle(tag)}
                >
                  <Text {...styles.filterChipText}>{tag}</Text>
                </Box>
              );
            })}
          </Flex>

          {isFilterOrPaginationLoading ? (
            <Skeleton height="16px" width="120px" flexShrink={0} />
          ) : (
            <Text
              as="span"
              className="blog-listing__results-count"
              role="status"
              {...styles.resultsCount}
              aria-atomic="true"
              aria-live="polite"
            >
              {intl.formatMessage(
                {
                  id: 'blog_listing.showing_count',
                  defaultMessage: 'Showing {count} {count, plural, one {editorial} other {editorials}}',
                },
                {count: showingCount}
              )}
            </Text>
          )}
        </Flex>
      )}

      {renderContent()}
    </Box>
    </>
  );
};

BlogListing.displayName = 'BlogListing';
BlogListing.propTypes = {
  component: PropTypes.shape({
    __typename: PropTypes.string,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
    title: PropTypes.string,
    filterTags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
